import React from 'react';
import { NextPage } from 'next';

import { logger } from '@belong/logging/logger';
import { getUrlFilename, withBasePath, withoutParams } from '@belong/url-utils';
import SectionArticleHeader from '@belong/ui-sections/sections/sectionArticleHeader/view';
import { ContentfulSection } from '@belong/ui-sections';

import type { PageServerSidePropsFunction } from 'src/core/types';
import { isPageError, ISupportPageData, SUPPORT_PAGE_TYPE } from 'src/core/types';
import SupportArticle from 'src/components/sections/SectionSupportArticle';
import { GLOBAL_SECTIONS } from 'src/models/sections';
import loadArticlePageData from './ArticlePage.data';

const ArticlePage: NextPage<ISupportPageData> = ({ data }: ISupportPageData) => {
  const { sections, article, rootCategory } = data;
  const { view: SectionSupportArticle, model } = SupportArticle;
  const articleProps = model(data);
  const rootSlug = rootCategory?.slug;

  const defaultHelpSection = sections[GLOBAL_SECTIONS.GET_HELP];
  const categoryHelpSection =
    {
      mobile: sections[GLOBAL_SECTIONS.GET_HELP_MOBILE],
      internet: sections[GLOBAL_SECTIONS.GET_HELP_INTERNET]
    }[rootSlug] || defaultHelpSection;

  return (
    <>
      <SectionArticleHeader {...article.header} />
      <SectionSupportArticle {...articleProps} />
      <ContentfulSection data-testid="section-get-help" {...categoryHelpSection} />
    </>
  );
};

export const getServerSidePageProps: PageServerSidePropsFunction = async (context, { config, host }) => {
  const path = withoutParams(context.resolvedUrl);

  const { categories, sections, internetAlert, mobileAlert } = config;
  const isCategoryUrl = !!categories.getCategoryByPath(path);
  const articleSlug = getUrlFilename(path);
  const category = categories.getCategoryByPath(path.replace(`/${articleSlug}`, ''));
  const rootCategory = categories.getRootCategory(path);

  // if this is a category page, return a 404
  if (isCategoryUrl) {
    logger.error(`Not an Article page: ${path}`);
    return { notFound: true };
  }

  // if the category path is invalid, return a 404
  if (!category) {
    logger.error(`Couldn't resolve category from url: ${path}`);
    return { notFound: true };
  }

  // if any part of the data load/transform fails, return an error (404)
  const pageData = await loadArticlePageData(categories, category, articleSlug);
  if (isPageError(pageData)) {
    logger.error(`Couldn't load article page data with articleSlug:${articleSlug}`);
    return { notFound: true };
  }

  const canonicalUrl = `https://${host}${withBasePath(category.path)}/${articleSlug}`;

  const alert = {
    mobile: mobileAlert,
    internet: internetAlert
  }[rootCategory.slug];

  return {
    meta: {
      canonicalUrl,
      description: pageData.article.metaDescription,
      keywords: pageData.article.metaKeywords,
      pageTitle: pageData.article.metaPageTitle,
      noIndex: pageData.article.metaNoIndex
    },
    data: {
      type: SUPPORT_PAGE_TYPE.article,
      sections,
      category,
      rootCategory,
      alert,
      ...pageData
    }
  };
};

export default ArticlePage;
